import React, { useState, useEffect } from 'react';

/**
 * Book Advertisement Components for "Letters to Evelyn"
 * Beautifully enlarged, diverse sizes, extremely witty sales copy
 * Strategic placement variants: Large, Medium, Small, Micro, Banner, Sidebar
 */

const WITTY_LINES = [
  "Truth is Stranger than Fiction -- and funnier too!",
  "More funny than Dave Chappelle... or your money back!",
  "BUT I SURVIVED -- and you'll laugh about it!",
  "50+ finely-crafted zany zingers inside!",
  "Can induce hurricane-force winds of laughter!",
  "A True Supernatural Thriller Comedy",
  "The comical side of it is exceedingly brilliant...",
  "People are laughing their butts off reading this!",
  "Never in my life did I think... and that's what makes it funny!",
  "Red Five standing by... for laughter!",
  "Warning: May cause spontaneous snort-laughing in public.",
  "Side effects include: uncontrollable giggling and missed bus stops.",
  "If you don't laugh, check your pulse.",
  "Funnier than your uncle's Thanksgiving stories -- and actually true.",
  "Supernatural events + dry wit = page-turner you can't put down.",
  "The book your therapist wishes they'd written.",
  "One reader laughed so hard they woke the dog. The dog also laughed.",
  "Optioned for Film -- Hollywood wants in on the joke.",
  "You'll read it in one sitting. Your couch has been warned.",
  "Part thriller, part comedy, entirely unforgettable.",
  "Like if Stephen King and Jerry Seinfeld had a literary baby.",
  "19 five-star reviews can't all be wrong. Or can they? (They can't.)",
  "The funniest true story you've never heard -- until now.",
  "Buy it for the laughs. Stay for the supernatural plot twists.",
  "Your bookshelf called. It said it's tired of boring books.",
  "Reading this on public transit? Prepare for judging stares from laughing too loud.",
  "Scientists confirm: this book is clinically hilarious.",
  "Now with 100% more paranormal activity than your average comedy.",
  "The only book that made my cat look at me with concern.",
  "Better than scrolling social media. And that's a VERY high bar.",
  "Alexa, order 'Letters to Evelyn' -- said everyone who read the first page.",
  "A literary achievement in making grown adults wheeze-laugh.",
  "Hollywood called. They want the movie rights. We're thinking about it."
];

const BOOK_LINKS = {
  amazon: 'https://play.google.com/store/books/details/John_Selman_Letters_to_Evelyn?id=KpByEQAAQBAJ&hl=en_US',
  kindle: 'https://play.google.com/store/books/details/John_Selman_Letters_to_Evelyn?id=KpByEQAAQBAJ&hl=en_US',
  freeRead: 'https://drive.google.com/file/d/1UhDwAs9VwUUoIinRNwnHFYZ0RlcvMBQI/view?usp=sharing',
  audiobook: 'https://play.google.com/store/audiobooks/details/John_Selman_Letters_to_Evelyn?id=AQAAAEAq4giiMM',
};

const getRandomLine = () => WITTY_LINES[Math.floor(Math.random() * WITTY_LINES.length)];

/**
 * LARGE AD — Cinematic hero banner with book mockup and animated quote rotation
 */
export const LargeBookAd = ({ variant = 1 }) => {
  const [line, setLine] = useState(getRandomLine());
  const [hovered, setHovered] = useState(false);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setFade(false);
      setTimeout(() => { setLine(getRandomLine()); setFade(true); }, 400);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const palettes = {
    1: { bg: '#FDFBF7', accent: '#047857', glow: 'rgba(252, 211, 77,0.18)', badge: '#fbbf24' },
    2: { bg: '#FDFBF7', accent: '#67e8f9', glow: 'rgba(34,211,238,0.18)', badge: '#38bdf8' },
    3: { bg: '#1a0f0a', accent: '#fbbf24', glow: 'rgba(251,191,36,0.18)', badge: '#F59E0B' }
  };
  const p = palettes[variant] || palettes[1];

  return (
    <div
      data-testid="large-book-ad"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: `linear-gradient(155deg, ${p.bg} 0%, ${p.bg}ee 60%, ${p.accent}12 100%)`,
        borderRadius: 18,
        padding: '36px 40px',
        marginBottom: 20,
        position: 'relative',
        overflow: 'hidden',
        border: `1px solid ${p.accent}18`,
        transition: 'transform 0.35s ease, box-shadow 0.35s ease',
        transform: hovered ? 'translateY(-3px)' : 'none',
        boxShadow: hovered
          ? `0 20px 60px rgba(0,0,0,0.5), 0 0 100px ${p.glow}`
          : '0 10px 40px rgba(0,0,0,0.3)'
      }}
    >
      {/* Noise texture */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.025,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
        pointerEvents: 'none'
      }} />

      {/* Glow orbs */}
      <div style={{
        position: 'absolute', top: -50, right: -50, width: 240, height: 240,
        borderRadius: '50%', background: p.glow, filter: 'blur(70px)', pointerEvents: 'none'
      }} />
      <div style={{
        position: 'absolute', bottom: -30, left: -30, width: 140, height: 140,
        borderRadius: '50%', background: `${p.accent}08`, filter: 'blur(50px)', pointerEvents: 'none'
      }} />

      <div style={{ position: 'relative', zIndex: 1, display: 'flex', gap: 32, alignItems: 'center' }}>
        {/* Book cover mockup */}
        <div style={{
          width: 140, height: 200, borderRadius: 10, flexShrink: 0, position: 'relative',
          background: `linear-gradient(145deg, #F3F1EC, #312e81, #F3F1EC)`,
          boxShadow: `8px 8px 30px rgba(0,0,0,0.6), -2px -2px 10px ${p.accent}15, 0 0 0 1px rgba(255,255,255,0.06)`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: 16, transition: 'transform 0.4s', transform: hovered ? 'rotate(-3deg) scale(1.04)' : 'none'
        }}>
          <span style={{ fontSize: '0.5rem', color: p.accent, textTransform: 'uppercase', letterSpacing: 3, opacity: 0.8 }}>A Novel By</span>
          <span style={{ fontSize: '1.05rem', color: '#047857', fontWeight: 800, textAlign: 'center', marginTop: 10, lineHeight: 1.25, letterSpacing: '-0.01em' }}>Letters to Evelyn</span>
          <span style={{ fontSize: '0.7rem', color: '#c4b5fd', marginTop: 10, fontWeight: 500 }}>John Selman</span>
          <div style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', background: p.badge, padding: '4px 14px', borderRadius: 5, fontSize: '0.52rem', fontWeight: 800, color: '#000', letterSpacing: 1, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Available Now</div>
        </div>

        {/* Content side */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ color: '#0F172A', fontSize: '1.5rem', fontWeight: 800, letterSpacing: '-0.03em' }}>Letters to Evelyn</span>
            <span style={{ background: `${p.badge}20`, padding: '3px 10px', borderRadius: 12, fontSize: '0.58rem', fontWeight: 700, color: p.badge, border: `1px solid ${p.badge}30` }}>19 Five-Star Reviews</span>
            <span style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', padding: '3px 10px', borderRadius: 12, fontSize: '0.56rem', fontWeight: 700, color: '#0F172A', animation: 'pulse 2s ease-in-out infinite' }}>Optioned for Film</span>
          </div>

          <p style={{
            color: p.accent, fontSize: '0.95rem', fontWeight: 500, fontStyle: 'italic',
            margin: '0 0 12px 0', lineHeight: 1.55, minHeight: 44,
            opacity: fade ? 1 : 0, transition: 'opacity 0.4s ease'
          }}>"{line}"</p>

          <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem', margin: '0 0 18px 0', lineHeight: 1.5 }}>
            A True Supernatural Thriller Comedy. Optioned for Film. The funniest true story you'll read this year.
          </p>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <a
              href={BOOK_LINKS.amazon} target="_blank" rel="noopener noreferrer"
              data-testid="large-ad-cta-primary"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: `linear-gradient(135deg, ${p.accent}ee, ${p.accent})`,
                color: '#000', padding: '12px 28px', borderRadius: 12,
                fontWeight: 800, fontSize: '0.85rem', textDecoration: 'none',
                transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: `0 6px 24px ${p.glow}`,
                letterSpacing: '-0.01em'
              }}
            >
              <i className="fas fa-book-open" style={{ fontSize: '0.75rem' }} />
              Get the Book on Google Play Books
            </a>
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem' }}>Available on Google Play</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * MEDIUM AD — Glass card with rotating tagline
 */
export const MediumBookAd = ({ variant = 1 }) => {
  const [line] = useState(getRandomLine());
  const [hovered, setHovered] = useState(false);

  const accents = { 1: '#047857', 2: '#67e8f9', 3: '#34d399', 4: '#fbbf24' };
  const accent = accents[variant] || accents[1];

  return (
    <div
      data-testid="medium-book-ad"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'rgba(255,255,255,0.025)',
        backdropFilter: 'blur(18px)',
        borderRadius: 14,
        padding: '20px 24px',
        marginBottom: 16,
        border: `1px solid rgba(255,255,255,0.06)`,
        transition: 'transform 0.3s, border-color 0.3s, box-shadow 0.3s',
        transform: hovered ? 'translateY(-2px)' : 'none',
        borderColor: hovered ? `${accent}35` : 'rgba(255,255,255,0.06)',
        boxShadow: hovered ? `0 12px 40px rgba(0,0,0,0.35), 0 0 50px ${accent}10` : 'none'
      }}
    >
      <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
        {/* Mini book */}
        <div style={{
          width: 64, height: 90, borderRadius: 7, flexShrink: 0,
          background: 'linear-gradient(145deg, #F3F1EC, #312e81)',
          boxShadow: '4px 4px 16px rgba(0,0,0,0.4)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 8,
          transition: 'transform 0.3s', transform: hovered ? 'rotate(-2deg)' : 'none'
        }}>
          <span style={{ fontSize: '0.5rem', color: accent, fontWeight: 700, textAlign: 'center', lineHeight: 1.2 }}>Letters to Evelyn</span>
          <span style={{ fontSize: '0.38rem', color: '#047857', marginTop: 4 }}>J. Selman</span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            color: accent, fontSize: '0.82rem', fontWeight: 500, fontStyle: 'italic',
            margin: '0 0 8px 0', lineHeight: 1.45,
            overflow: 'hidden', textOverflow: 'ellipsis',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
          }}>"{line}"</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#64748B', fontSize: '0.68rem' }}>Supernatural Thriller Comedy</span>
            <span style={{ color: '#047857', fontSize: '0.6rem', fontWeight: 600 }}>19 Five-Star Reviews</span>
          </div>
        </div>
        <a
          href={BOOK_LINKS.amazon} target="_blank" rel="noopener noreferrer"
          data-testid="medium-ad-cta"
          style={{
            background: `${accent}15`, border: `1px solid ${accent}30`,
            color: accent, padding: '8px 18px', borderRadius: 10,
            fontWeight: 700, fontSize: '0.72rem', textDecoration: 'none',
            whiteSpace: 'nowrap', transition: 'background 0.2s, transform 0.2s',
            transform: hovered ? 'scale(1.05)' : 'none'
          }}
        >
          <i className="fas fa-arrow-right" style={{ fontSize: '0.6rem', marginRight: 5 }} />
          Get Book
        </a>
      </div>
    </div>
  );
};

/**
 * SMALL AD — Compact inline bar with accent stripe
 */
export const SmallBookAd = ({ variant = 1 }) => {
  const [hovered, setHovered] = useState(false);
  const accents = { 1: '#047857', 2: '#67e8f9', 3: '#34d399', 4: '#fbbf24' };
  const accent = accents[variant] || accents[1];
  const [line] = useState(WITTY_LINES[Math.floor(Math.random() * 8)]);

  return (
    <a
      href={BOOK_LINKS.amazon} target="_blank" rel="noopener noreferrer"
      data-testid="small-book-ad"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        background: hovered ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(8px)',
        borderRadius: 11, padding: '10px 16px', marginBottom: 12,
        textDecoration: 'none', border: `1px solid ${hovered ? accent + '25' : 'rgba(255, 255, 255, 0.85)'}`,
        transition: 'all 0.25s ease',
        transform: hovered ? 'translateX(2px)' : 'none'
      }}
    >
      <div style={{
        width: 5, height: 28, borderRadius: 3, background: accent, flexShrink: 0,
        boxShadow: `0 0 8px ${accent}40`
      }} />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ color: 'rgba(255,255,255,0.88)', fontWeight: 700, fontSize: '0.78rem' }}>Letters to Evelyn</span>
        <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>{line}</span>
      </div>
      <span style={{
        background: `${accent}15`, border: `1px solid ${accent}25`,
        color: accent, padding: '4px 12px', borderRadius: 8,
        fontSize: '0.65rem', fontWeight: 700, flexShrink: 0
      }}>Read</span>
    </a>
  );
};

/**
 * MICRO AD — Pill badge, great for inline placement
 */
export const MicroBookAd = () => (
  <a
    href={BOOK_LINKS.amazon} target="_blank" rel="noopener noreferrer"
    data-testid="micro-book-ad"
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: 'rgba(253, 224, 71, 0.08)',
      borderRadius: 20, padding: '4px 12px', textDecoration: 'none',
      border: '1px solid rgba(253, 224, 71, 0.14)', transition: 'all 0.2s'
    }}
  >
    <span style={{ color: '#047857', fontSize: '0.68rem', fontWeight: 600 }}>Letters to Evelyn</span>
    <span style={{ color: '#047857', fontSize: '0.56rem', fontWeight: 700 }}>Read</span>
  </a>
);

/**
 * BANNER AD — Full-width thin strip for between sections
 */
export const BannerBookAd = ({ variant = 1 }) => {
  const [line] = useState(getRandomLine());
  const accents = { 1: '#047857', 2: '#67e8f9', 3: '#fbbf24' };
  const accent = accents[variant] || accents[1];

  return (
    <a
      href={BOOK_LINKS.amazon} target="_blank" rel="noopener noreferrer"
      data-testid="banner-book-ad"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
        background: `linear-gradient(90deg, ${accent}08 0%, ${accent}14 50%, ${accent}08 100%)`,
        borderRadius: 10, padding: '10px 20px', marginBottom: 14,
        textDecoration: 'none',
        border: `1px solid ${accent}12`,
        transition: 'all 0.2s'
      }}
    >
      <span style={{ color: accent, fontSize: '0.72rem', fontWeight: 700 }}>Letters to Evelyn</span>
      <span style={{ color: '#64748B', fontSize: '0.65rem', fontStyle: 'italic', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>"{line}"</span>
      <span style={{
        background: `${accent}18`, border: `1px solid ${accent}28`,
        color: accent, padding: '3px 12px', borderRadius: 8,
        fontSize: '0.6rem', fontWeight: 800, letterSpacing: 0.5
      }}>GET BOOK</span>
    </a>
  );
};

/**
 * SIDEBAR AD — Vertical card for sidebar placement
 */
export const SidebarBookAd = ({ variant = 1 }) => {
  const [line, setLine] = useState(getRandomLine());
  const [hovered, setHovered] = useState(false);
  const accents = { 1: '#047857', 2: '#67e8f9', 3: '#34d399' };
  const accent = accents[variant] || accents[1];

  useEffect(() => {
    const t = setInterval(() => setLine(getRandomLine()), 10000);
    return () => clearInterval(t);
  }, []);

  return (
    <a
      href={BOOK_LINKS.amazon} target="_blank" rel="noopener noreferrer"
      data-testid="sidebar-book-ad"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
        background: hovered ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(12px)',
        borderRadius: 14, padding: '20px 16px', marginBottom: 14,
        textDecoration: 'none',
        border: `1px solid ${hovered ? accent + '30' : 'rgba(255, 255, 255, 0.85)'}`,
        transition: 'all 0.3s',
        transform: hovered ? 'translateY(-2px)' : 'none',
        boxShadow: hovered ? `0 8px 30px ${accent}10` : 'none'
      }}
    >
      {/* Mini book cover */}
      <div style={{
        width: 80, height: 110, borderRadius: 8, flexShrink: 0,
        background: 'linear-gradient(145deg, #F3F1EC, #312e81)',
        boxShadow: '4px 4px 20px rgba(0,0,0,0.5)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: 10, transition: 'transform 0.3s', transform: hovered ? 'scale(1.05)' : 'none'
      }}>
        <span style={{ fontSize: '0.6rem', color: accent, fontWeight: 700, textAlign: 'center', lineHeight: 1.25 }}>Letters to Evelyn</span>
        <span style={{ fontSize: '0.42rem', color: '#c4b5fd', marginTop: 5 }}>John Selman</span>
      </div>

      <p style={{
        color: '#334155', fontSize: '0.68rem', fontStyle: 'italic',
        textAlign: 'center', margin: 0, lineHeight: 1.45, minHeight: 34
      }}>"{line}"</p>

      <span style={{
        background: `${accent}18`, border: `1px solid ${accent}28`,
        color: accent, padding: '6px 16px', borderRadius: 8,
        fontSize: '0.65rem', fontWeight: 700
      }}>Get the Book</span>
    </a>
  );
};

/**
 * AD PLACEMENT HELPER
 */
export const BookAdPlacement = ({ size = 'small', variant = 1 }) => {
  switch(size) {
    case 'large': return <LargeBookAd variant={variant} />;
    case 'medium': return <MediumBookAd variant={variant} />;
    case 'small': return <SmallBookAd variant={variant} />;
    case 'micro': return <MicroBookAd />;
    case 'banner': return <BannerBookAd variant={variant} />;
    case 'sidebar': return <SidebarBookAd variant={variant} />;
    default: return <SmallBookAd variant={variant} />;
  }
};

export default { LargeBookAd, MediumBookAd, SmallBookAd, MicroBookAd, BannerBookAd, SidebarBookAd, BookAdPlacement };
